import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useCompany } from './useCompany';

export function useUnreadOrdersCount() {
  const { company } = useCompany();
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchUnreadCount = useCallback(async () => {
    if (!company?.id) {
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    try {
      const { count, error } = await supabase
        .from('seller_order_notifications')
        .select('id', { count: 'exact', head: true })
        .eq('seller_company_id', company.id)
        .eq('is_read', false);

      if (error) {
        console.error('[useUnreadOrdersCount] Error:', error);
        setUnreadCount(0);
      } else {
        setUnreadCount(count ?? 0);
      }
    } catch (err) {
      console.error('[useUnreadOrdersCount] Exception:', err);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  }, [company?.id]);

  // Mark a specific order as read
  const markAsRead = useCallback(async (orderId: string) => {
    if (!company?.id) return;

    try {
      const { error } = await supabase
        .from('seller_order_notifications')
        .update({
          is_read: true,
          first_viewed_at: new Date().toISOString()
        })
        .eq('seller_company_id', company.id)
        .eq('order_id', orderId);

      if (error) {
        console.error('[useUnreadOrdersCount] Error marking as read:', error);
      } else {
        // Optimistically update count
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('[useUnreadOrdersCount] Exception marking as read:', err);
    }
  }, [company?.id]);

  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  // Subscribe to realtime changes with debouncing
  useEffect(() => {
    if (!company?.id) return;

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const channel = supabase
      .channel(`seller-notifications-${company.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'seller_order_notifications',
          filter: `seller_company_id=eq.${company.id}`
        },
        () => {
          // Debounce to prevent rapid-fire refetches
          if (debounceTimer) clearTimeout(debounceTimer);
          debounceTimer = setTimeout(() => {
            fetchUnreadCount();
          }, 300);
        }
      )
      .subscribe();

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      supabase.removeChannel(channel);
    };
  }, [company?.id, fetchUnreadCount]);

  // Refetch on window focus
  useEffect(() => {
    const handleFocus = () => {
      fetchUnreadCount();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [fetchUnreadCount]);

  return {
    unreadCount,
    loading,
    refetch: fetchUnreadCount,
    markAsRead
  };
}
